/**
 * MuseFrame — the one onError idiom for every muse image site (hero, editorial plates, chooser
 * cards, header/hub thumbs). Frames are operator-uploaded via `objects/`'s `muse-samples-push`
 * (see `thinking/plans/2026_08_23_ovid_muses.md`, "Frames") and may not exist yet for a given
 * muse — a 404 must degrade to the panel ground, never a broken-image icon. The pack surfaces
 * have no equivalent idiom (grep, 2026-08-23: no `onError` anywhere under `products/ovid`), so
 * this is new, small, and shared rather than duplicated per call site.
 *
 * Degrade contract: on error (or an empty src — a muse of her own that has no photo yet), renders an empty `<span>` with the same `className` — the caller's
 * own CSS (aspect-ratio + `background: var(--ov-panel)`/`var(--ov-raise)`) is what actually reads
 * as "the panel ground". This component never guesses a background of its own.
 */
import {useState} from 'react'

export interface MuseFrameProps {
    src: string
    alt: string
    className?: string
    loading?: 'lazy' | 'eager'
}

export function MuseFrame({src, alt, className, loading = 'lazy'}: MuseFrameProps) {
    const [errored, setErrored] = useState(false)

    if (errored || !src) {
        return <span className={className} aria-hidden="true" />
    }

    return (
        <img
            src={src}
            alt={alt}
            className={className}
            loading={loading}
            decoding="async"
            onError={() => setErrored(true)}
        />
    )
}

export default MuseFrame
